import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { rupee, statusColor } from "@/lib/format";
import ListToolbar from "@/components/ListToolbar";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const TYPE_LABEL = { subscription: "Subscription", slot: "Additional Slots", renewal: "Renewal" };
const STATUS_LABEL = { pending: "Pending", screenshot_uploaded: "Under Review", approved: "Approved", rejected: "Rejected" };

export default function Billing() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ type: "", status: "" });
  const [sort, setSort] = useState("latest");

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (search) p.set("q", search);
    if (filters.type) p.set("type", filters.type);
    if (filters.status) p.set("status", filters.status);
    p.set("sort", sort);
    const { data } = await api.get(`/billing?${p.toString()}`);
    setItems(data);
  }, [search, filters, sort]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-extrabold">Billing</h1>
        <p className="text-muted-foreground text-sm font-medium">Your subscription, renewal and slot payments.</p>
      </div>

      <ListToolbar
        placeholder="Search invoice number…"
        search={search} setSearch={setSearch}
        filters={[
          { key: "type", label: "Category", value: filters.type, options: Object.entries(TYPE_LABEL).map(([v, l]) => ({ value: v, label: l })) },
          { key: "status", label: "Status", value: filters.status, options: Object.entries(STATUS_LABEL).map(([v, l]) => ({ value: v, label: l })) },
        ]}
        onFilter={(k, v) => setFilters((f) => ({ ...f, [k]: v }))}
        sort={sort} setSort={setSort}
        sorts={[{ value: "latest", label: "Latest" }, { value: "oldest", label: "Oldest" }, { value: "highest", label: "Highest Amount" }]}
        onReset={() => { setSearch(""); setFilters({ type: "", status: "" }); setSort("latest"); }}
      />

      <div className="bg-card border rounded-xl overflow-x-auto ct-scroll">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead><TableHead>Category</TableHead><TableHead>Package</TableHead>
              <TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`billing-row-${p.id}`}>
                <TableCell className="font-mono text-xs">{p.invoice_no}</TableCell>
                <TableCell className="font-semibold">{TYPE_LABEL[p.type]}</TableCell>
                <TableCell>{p.breakdown?.products ? `${p.breakdown.products} Products` : "—"}</TableCell>
                <TableCell className="font-bold">{rupee(p.amount)}</TableCell>
                <TableCell><Badge className={`border ${statusColor(p.status)}`}>{STATUS_LABEL[p.status]}</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString("en-IN")}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground font-semibold">No billing records.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
